import { Download, RemoveRedEye } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    exportarArchivoCancerigenos,
    getInfoEstructuraArchivoCancerigeno,
    getNominasActivasEstablecimiento
} from '../../../../../redux/actions/resolucion';
import { convertDateToNet } from '../../../../../util/datetime';
import { downloadFile } from '../../../../../util/files';
import DropZone from '../../../../commons/dropZone/DropZone';
import PdfDialog from '../../../../commons/file/PdfDialog';
import FeatureLabel from '../../../../prevencion/nte/commons/labels/FeatureLabel';
import ImportarNominasErrorTable from './ImportarNominasErrorTable';
import { validateExcelData } from './importarNominasValidation';
import { parseExcelFile } from './parseExcelFile';

const ImportarNominasSection = ({ onImportSuccess }) => {
    const dispatch = useDispatch();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isDownloadingNominas, setIsDownloadingNominas] = useState(false);

    // Data from Redux for validation
    const sectores = useSelector((state) => state.resolucion.sectores.data);
    const puestos = useSelector((state) => state.resolucion.puestos.data);
    const sustanciasFormulario = useSelector((state) => state.resolucion.postResolucion.sustancias);
    const sustanciasDisponibles = useSelector((state) => state.resolucion.sustancias.data);
    const puestosAsociados = useSelector((state) => state.resolucion.postResolucion.puestos);
    const idDependenciaEmpleador = useSelector((state) => state.resolucion.searchedEstablecimiento);
    const datosEstablecimiento = useSelector((state) => state.resolucion.datosEstablecimiento?.data);
    const pdfInfoArchivo = useSelector((state) => state.resolucion.infoEstructuraArchivoCancerigeno?.data?.base64);

    const handleUpload = async () => {
        if (!file || file.length === 0) return;

        setLoading(true);
        setErrors([]);
        setShowErrors(false);

        try {
            // Decode base64 back to binary for xlsx
            const base64 = file[0].value;
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const rows = parseExcelFile(bytes);

            // 1. Fetch active nominations from backend
            const today = convertDateToNet(new Date());
            const response = await dispatch(getNominasActivasEstablecimiento(idDependenciaEmpleador, today));
            const currentNominas = response.value.data;

            // 2. Validate with fetched data
            const result = validateExcelData(rows, {
                sectores,
                puestos,
                sustanciasFormulario,
                sustanciasDisponibles,
                puestosAsociados,
                nominasActivas: currentNominas,
                establecimientoMU: datosEstablecimiento?.matrizUnica
            });

            if (!result.valid) {
                setErrors(result.errors);
                setShowErrors(true);
            } else {
                setErrors([]);
                setShowErrors(false);
                onImportSuccess(result.workers);
                setFile(null);
            }
        } catch (err) {
            setErrors([{ cuil: 'GENERAL', messages: [`Error al procesar la importación: ${err.message}`] }]);
            setShowErrors(true);
        } finally {
            setLoading(false);
        }
    };

    const handleReintentar = () => {
        setErrors([]);
        setShowErrors(false);
        setFile(null);
    };

    const handleDownloadNominas = () => {
        if (!idDependenciaEmpleador) {
            toast.error('No hay establecimiento seleccionado');
            return;
        }
        if (isDownloadingNominas) {
            return; // Prevenir múltiples descargas simultáneas
        }
        setIsDownloadingNominas(true);
        dispatch(exportarArchivoCancerigenos(idDependenciaEmpleador, datosEstablecimiento?.matrizUnica))
            .then((res) => {
                downloadFile(res?.value?.data, res.value.fileName);
                setIsDownloadingNominas(false);
            })
            .catch((err) => {
                toast.error('Error al descargar el archivo de nóminas');
                setIsDownloadingNominas(false);
            });
    };

    const handleShowInfo = () => {
        setShowInfo(true);
        dispatch(getInfoEstructuraArchivoCancerigeno());
    };

    // Show error table view
    if (showErrors && errors.length > 0) {
        return (
            <ImportarNominasErrorTable
                data={errors}
                handleReintentar={handleReintentar}
            />
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container sx={{ px: 2.5, mb: 1, justifyContent: 'space-around' }}>
                <Grid item>
                    <FeatureLabel
                        labelText="Información del Archivo a Importar"
                        color="primary"
                        icon={<RemoveRedEye />}
                        onClick={handleShowInfo}
                        tooltipText="Información del Archivo a Importar"
                    />
                </Grid>
                <Grid item>
                    <FeatureLabel
                        labelText="Descargar Último Estado de Nóminas"
                        icon={<Download />}
                        onClick={handleDownloadNominas}
                        tooltipText="Descargar último estado de nóminas"
                    />
                </Grid>
            </Grid>

            <DropZone
                onUpload={handleUpload}
                file={file}
                setFile={setFile}
                loading={loading}
                extension=".xlsx"
                accept={{
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    'application/vnd.ms-excel': ['.xls'],
                }}
                dropZoneText="Arrastre la nómina en formato .xlsx o .xls aquí. O haga click en el recuadro para adjuntarla."
                dropZoneEditText="Si desea cambiar el archivo arrástrelo nuevamente o haga click en el recuadro."
            />

            {/* Dialog de información */}
            <PdfDialog
                title="Información del Archivo a Importar"
                file={pdfInfoArchivo}
                open={showInfo}
                handleClose={() => setShowInfo(false)}
                controllers={true}
            />
        </Box>
    );
};

export default ImportarNominasSection;
